import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { ThemeToggle } from '@/components/ThemeToggle'
import { NotificationCenter } from '@/components/NotificationCenter'

// Map route segments to human-readable titles
const ROUTE_LABELS: Record<string, string> = {
  '': 'Dashboard',
  dashboard: 'Dashboard',
  stream: 'Stream',
  analytics: 'Analytics',
  overlay: 'Overlay',
  alerts: 'Alerts',
  chatbot: 'Chatbot',
  loyalty: 'Loyalty',
  connections: 'Connections',
  settings: 'Settings',
  billing: 'Billing',
  profile: 'Profile',
  notifications: 'Notifications',
}

function humanize(segment: string): string {
  return (
    ROUTE_LABELS[segment] ??
    segment
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  )
}

function getPageTitle(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length === 0) return 'Dashboard'
  return humanize(segments[segments.length - 1])
}

// Parent crumb for nested routes (e.g. /alerts/editor -> "Alerts"). Returns null
// for top-level pages so the title stands alone.
function getParentCrumb(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean)
  if (segments.length < 2) return null
  return humanize(segments[segments.length - 2])
}

function ChevronDownIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  )
}

interface AvatarProps {
  avatarUrl?: string
  displayName: string
  size?: number
}

function Avatar({ avatarUrl, displayName, size = 32 }: AvatarProps) {
  const initials = displayName
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={displayName}
        width={size}
        height={size}
        style={{ width: size, height: size }}
        className="rounded-full object-cover shrink-0"
      />
    )
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        background: 'linear-gradient(135deg, var(--accent-light), var(--accent-hover))',
        fontSize: size * 0.38,
      }}
      className="rounded-full flex items-center justify-center text-white font-semibold shrink-0 select-none"
      aria-hidden="true"
    >
      {initials}
    </div>
  )
}

interface TopBarProps {
  onHamburgerClick?: () => void
}

export default function TopBar({ onHamburgerClick }: TopBarProps = {}) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const pageTitle = getPageTitle(location.pathname)
  const parentCrumb = getParentCrumb(location.pathname)

  // Close dropdown on click outside
  useEffect(() => {
    if (!dropdownOpen) return
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [dropdownOpen])

  // Close dropdown on Escape
  useEffect(() => {
    if (!dropdownOpen) return
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setDropdownOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [dropdownOpen])

  function handleLogout() {
    setDropdownOpen(false)
    logout()
    navigate('/login')
  }

  return (
    <header
      className="flex items-center justify-between px-4 md:px-6 shrink-0"
      style={{
        height: 56,
        backgroundColor: 'var(--bg-primary)',
        borderBottom: '1px solid var(--border-primary)',
      }}
    >
      {/* Mobile hamburger — hidden on md+ since the sidebar is always visible */}
      {onHamburgerClick && (
        <button
          type="button"
          onClick={onHamburgerClick}
          aria-label="Open navigation menu"
          className="md:hidden flex flex-col justify-center gap-1.5 w-10 h-10 -ml-1 mr-1 rounded-lg text-gray-400 hover:text-white transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
        >
          <span className="block h-0.5 w-5 rounded" style={{ backgroundColor: 'currentColor' }} aria-hidden="true" />
          <span className="block h-0.5 w-5 rounded" style={{ backgroundColor: 'currentColor' }} aria-hidden="true" />
          <span className="block h-0.5 w-5 rounded" style={{ backgroundColor: 'currentColor' }} aria-hidden="true" />
        </button>
      )}

      {/* Left: breadcrumb + page title */}
      <div className="flex flex-col justify-center min-w-0 flex-1">
        {parentCrumb && (
          <nav aria-label="Breadcrumb" className="leading-none mb-1 hidden sm:block">
            <span
              className="text-[11px] font-medium tracking-wide"
              style={{ color: 'var(--text-muted)' }}
            >
              {parentCrumb}
              <span aria-hidden="true" className="mx-1.5 opacity-60">
                /
              </span>
            </span>
          </nav>
        )}
        <h1
          className="text-base md:text-lg font-semibold tracking-tight leading-none truncate"
          style={{ color: 'var(--text-primary)' }}
        >
          {pageTitle}
        </h1>
      </div>

      {/* Right: theme toggle + notification bell + user dropdown */}
      <div className="flex items-center gap-1.5">
        {/* Theme toggle — light / system / dark */}
        <ThemeToggle />

        {/* Notification center — bell + badge + grouped dropdown panel */}
        <NotificationCenter />

        {/* Divider — separates utility controls from identity */}
        {user && (
          <span
            aria-hidden="true"
            className="mx-1.5 w-px h-6 self-center"
            style={{ backgroundColor: 'var(--border-primary)' }}
          />
        )}

        {/* User dropdown */}
        {user && (
          <div className="relative" ref={dropdownRef}>
            <button
              type="button"
              onClick={() => setDropdownOpen((v) => !v)}
              aria-haspopup="true"
              aria-expanded={dropdownOpen}
              className="flex items-center gap-2 rounded-lg pl-1.5 pr-2 py-1.5 transition-colors hover:bg-white/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-500"
            >
              <span
                className="rounded-full ring-1 shrink-0 flex"
                style={{ ['--tw-ring-color' as string]: 'rgba(255,255,255,0.12)' }}
              >
                <Avatar avatarUrl={user.avatarUrl} displayName={user.displayName} size={30} />
              </span>
              <span
                className="text-sm font-medium leading-none max-w-[120px] truncate hidden lg:inline"
                style={{ color: 'var(--text-primary)' }}
              >
                {user.displayName}
              </span>
              <span
                className="transition-transform duration-150"
                style={{
                  color: 'var(--text-muted)',
                  transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              >
                <ChevronDownIcon />
              </span>
            </button>

            {dropdownOpen && (
              <div
                className="absolute right-0 top-full mt-2 w-56 rounded-xl py-1.5 shadow-2xl z-50 origin-top-right animate-fade-in"
                style={{
                  backgroundColor: 'var(--bg-card)',
                  border: '1px solid var(--border-primary)',
                }}
                role="menu"
                aria-label="User menu"
              >
                {/* User info header */}
                <div className="flex items-center gap-3 px-3 py-2.5 mb-1 border-b" style={{ borderColor: 'var(--border-primary)' }}>
                  <span
                    className="rounded-full ring-1 shrink-0 flex"
                    style={{ ['--tw-ring-color' as string]: 'rgba(255,255,255,0.1)' }}
                  >
                    <Avatar avatarUrl={user.avatarUrl} displayName={user.displayName} size={36} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate leading-tight" style={{ color: 'var(--text-primary)' }}>
                      {user.displayName}
                    </p>
                    {user.email && (
                      <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        {user.email}
                      </p>
                    )}
                  </div>
                </div>

                {/* Menu items */}
                {[
                  { label: 'Profile', to: '/profile' },
                  { label: 'Settings', to: '/settings' },
                  { label: 'Billing', to: '/billing' },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    role="menuitem"
                    onClick={() => setDropdownOpen(false)}
                    className="block mx-1 px-2.5 py-2 text-sm rounded-lg transition-colors hover:bg-white/5"
                    style={{ color: 'var(--text-secondary)' }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-light)')}
                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-secondary)')}
                  >
                    {item.label}
                  </Link>
                ))}

                <div className="my-1.5 mx-3" style={{ borderTop: '1px solid var(--border-primary)' }} />

                <button
                  type="button"
                  role="menuitem"
                  onClick={handleLogout}
                  className="w-full text-left mx-1 px-2.5 py-2 text-sm rounded-lg transition-colors hover:bg-white/5"
                  style={{ width: 'calc(100% - 0.5rem)', color: 'var(--text-muted)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--danger)')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-muted)')}
                >
                  Log Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
